import gsap from "gsap";
import { el } from "../lib/dom";
import { api } from "../lib/api";
import { sectionCover } from "../lib/sectionCover";
import { wedding } from "../content/site";
import { photos } from "../content/photos.generated";
import { confettiBurst } from "../lib/confetti";
import { buzz } from "../lib/haptics";

export function renderRsvp(root: HTMLElement) {
  const page = el("section", { class: "page", id: "rsvp" });

  page.appendChild(
    sectionCover({
      title: "RSVP",
      photo: photos.gallery[20] ?? photos.gallery[0] ?? null,  // IMG_4797
    }),
  );

  const content = el("div", { class: "page__content" });
  content.appendChild(
    el(
      "p",
      { class: "page__lead" },
      `Laat ons weet voor ${wedding.cutoffLabel}. Jy kan jou antwoord later opdateer met dieselfde e-pos.`,
    ),
  );

  const slot = el("div", { class: "rsvp" });
  content.appendChild(slot);
  page.appendChild(content);
  root.appendChild(page);

  renderForm(slot);

  gsap.from(slot, { opacity: 0, y: 20, duration: 0.7, ease: "power3.out", delay: 0.2 });
}

function renderForm(slot: HTMLElement) {
  slot.innerHTML = "";
  const form = el("form", { class: "rsvp__form" });

  const nameField = el("div", { class: "rsvp__field" });
  nameField.appendChild(el("label", { class: "rsvp__label", for: "rsvp-name" }, "Volle naam"));
  const nameInput = el("input", {
    id: "rsvp-name",
    type: "text",
    class: "rsvp__input",
    autocomplete: "name",
    required: true,
    placeholder: "Naam en van",
  }) as HTMLInputElement;
  nameField.appendChild(nameInput);
  form.appendChild(nameField);

  const emailField = el("div", { class: "rsvp__field" });
  emailField.appendChild(el("label", { class: "rsvp__label", for: "rsvp-email" }, "E-pos"));
  const emailInput = el("input", {
    id: "rsvp-email",
    type: "email",
    class: "rsvp__input",
    autocomplete: "email",
    required: true,
    placeholder: "naam@example.com",
  }) as HTMLInputElement;
  emailField.appendChild(emailInput);
  form.appendChild(emailField);

  const attend = el("div", { class: "rsvp__attend", style: "margin-top: 1rem;" });
  let status: "attending" | "declined" = "attending";
  for (const opt of [
    { v: "attending" as const, label: "Ek kom" },
    { v: "declined" as const, label: "Ek kan nie kom nie" },
  ]) {
    const id = `rsvp-status-${opt.v}`;
    const lbl = el("label", { for: id });
    const radio = el("input", {
      type: "radio",
      name: "rsvp-status",
      id,
      value: opt.v,
      checked: opt.v === "attending",
    }) as HTMLInputElement;
    radio.addEventListener("change", () => {
      if (radio.checked) {
        status = opt.v;
        toggleAttendingFields(opt.v);
      }
    });
    lbl.appendChild(radio);
    lbl.appendChild(el("span", {}, opt.label));
    attend.appendChild(lbl);
  }
  form.appendChild(attend);

  // Song request + extra guests — only relevant if attending; hidden on "declined"
  const attendingFields = el("div", { "data-attending-fields": "1" });

  /* Aantal addisionele gaste — 0 to 4, reveals that many name inputs */
  const extrasField = el("div", { class: "rsvp__field", style: "margin-top: 1.5rem;" });
  extrasField.appendChild(
    el("label", { class: "rsvp__label", for: "rsvp-extras-count" }, "Aantal addisionele gaste"),
  );
  const extrasCountInput = el("select", {
    id: "rsvp-extras-count",
    class: "rsvp__select",
  }) as HTMLSelectElement;
  for (let n = 0; n <= 4; n++) {
    const opt = el("option", { value: String(n) }, String(n)) as HTMLOptionElement;
    extrasCountInput.appendChild(opt);
  }
  extrasField.appendChild(extrasCountInput);
  attendingFields.appendChild(extrasField);

  /* Container that fills with N name inputs as the count changes */
  const extrasList = el("div", { "data-extras-list": "1" });
  attendingFields.appendChild(extrasList);

  const extraInputs: HTMLInputElement[] = [];
  function renderExtraNameFields(n: number) {
    extrasList.innerHTML = "";
    extraInputs.length = 0;
    for (let i = 0; i < n; i++) {
      const field = el("div", { class: "rsvp__field", style: "margin-top: 0.75rem;" });
      field.appendChild(
        el("label", { class: "rsvp__label", for: `rsvp-extra-${i}` }, `Naam van gas ${i + 1}`),
      );
      const input = el("input", {
        id: `rsvp-extra-${i}`,
        type: "text",
        class: "rsvp__input",
        placeholder: "Naam en van",
      }) as HTMLInputElement;
      field.appendChild(input);
      extraInputs.push(input);
      extrasList.appendChild(field);
    }
  }
  extrasCountInput.addEventListener("change", () => {
    renderExtraNameFields(parseInt(extrasCountInput.value, 10) || 0);
  });

  const songField = el("div", { class: "rsvp__field", style: "margin-top: 1.5rem;" });
  songField.appendChild(el("label", { class: "rsvp__label", for: "rsvp-song" }, "Versoekliedjie vir die DJ"));
  const songInput = el("input", {
    id: "rsvp-song",
    type: "text",
    class: "rsvp__input",
    placeholder: "Liedjie & kunstenaar",
  }) as HTMLInputElement;
  songField.appendChild(songInput);
  attendingFields.appendChild(songField);

  form.appendChild(attendingFields);

  const error = el("p", {
    class: "rsvp__error",
    style: "color: var(--color-accent); margin-top: 1rem; display: none;",
  });
  form.appendChild(error);

  const submit = el("button", {
    class: "rsvp__submit",
    type: "submit",
    "data-cursor-label": "Stuur",
  }, "Stuur RSVP") as HTMLButtonElement;
  form.appendChild(submit);

  function toggleAttendingFields(s: "attending" | "declined") {
    attendingFields.style.display = s === "attending" ? "" : "none";
  }

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    submit.disabled = true;
    submit.textContent = "Stuur...";
    error.style.display = "none";

    const name = nameInput.value.trim();
    const email = emailInput.value.trim();
    if (!name || !email) {
      error.textContent = "Vul asseblief jou naam en e-pos in.";
      error.style.display = "block";
      submit.disabled = false;
      submit.textContent = "Stuur RSVP";
      return;
    }

    const extraNames =
      status === "attending"
        ? extraInputs.map((i) => i.value.trim()).filter((n) => n.length > 0)
        : [];

    try {
      const result = await api.submitRsvp({
        fullName: name,
        email,
        status,
        ...(status === "attending" && songInput.value.trim()
          ? { songRequest: songInput.value.trim() }
          : {}),
        ...(extraNames.length ? { additionalGuests: extraNames } : {}),
      });
      renderSuccess(slot, name, result.status, result.isUpdate, extraNames.length);
    } catch (err) {
      error.textContent = err instanceof Error ? err.message : "Iets het verkeerd geloop.";
      error.style.display = "block";
      submit.disabled = false;
      submit.textContent = "Stuur RSVP";
    }
  });

  slot.appendChild(form);
  // preventScroll: focusing an off-screen input would otherwise auto-scroll
  // the browser to the RSVP section on initial page load.
  setTimeout(() => nameInput.focus({ preventScroll: true }), 100);
}

function renderSuccess(
  slot: HTMLElement,
  name: string,
  status: "attending" | "declined",
  isUpdate: boolean,
  extrasCount = 0,
) {
  slot.innerHTML = "";
  const headline = isUpdate ? "Opgedateer." : "Dankie!";
  const lead =
    status === "attending"
      ? extrasCount > 0
        ? `${name}, ons sien uit om jou en jou ${extrasCount === 1 ? "gas" : `${extrasCount} gaste`} daar te sien.`
        : `${name}, ons sien uit om jou daar te sien.`
      : `${name}, ons gaan jou mis — dankie dat jy laat weet het.`;

  // Soft check icon — animates in before the copy
  const check = el("div", { class: "rsvp__success-check", "aria-hidden": "true" });
  check.innerHTML = `<svg viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="32" cy="32" r="28" stroke-dasharray="176" stroke-dashoffset="176" class="rsvp__check-circle"/>
    <path d="M20 33 L29 42 L45 24" stroke-dasharray="40" stroke-dashoffset="40" class="rsvp__check-tick"/>
  </svg>`;

  const wrap = el(
    "div",
    { class: "rsvp__success" },
    check,
    el("h2", {}, headline),
    el(
      "p",
      { style: "margin-top: 1rem; color: var(--color-ink-soft);" },
      lead,
    ),
    el(
      "p",
      { style: "margin-top: 0.5rem; color: var(--color-ink-soft); font-size: var(--type-sm);" },
      "Jy kan jou antwoord later verander deur weer met dieselfde e-pos te stuur.",
    ),
  );
  slot.appendChild(wrap);

  // Animate check, then copy — confetti only when attending
  const tl = gsap.timeline();
  tl.from(check, { opacity: 0, scale: 0.7, duration: 0.5, ease: "back.out(2)" })
    .to(check.querySelector(".rsvp__check-circle"), { strokeDashoffset: 0, duration: 0.6, ease: "power2.out" }, "-=0.1")
    .to(check.querySelector(".rsvp__check-tick"), { strokeDashoffset: 0, duration: 0.4, ease: "power2.out" }, "-=0.25")
    .from(wrap.querySelectorAll("h2, p"), { opacity: 0, y: 16, stagger: 0.08, duration: 0.5, ease: "power3.out" }, "-=0.2");

  buzz("success");
  if (status === "attending") {
    setTimeout(() => confettiBurst(check, { count: 100, durationMs: 1800 }), 400);
  }
}
